import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Lock, Unlock, RotateCcw, UserX, Loader2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EmergencyPanel = () => {
  const [lockdown, setLockdown] = useState(null);
  const [busy, setBusy] = useState("");
  const [rotateLog, setRotateLog] = useState(null);

  const load = () => axios.get(`${API}/security/lockdown`).then(r => setLockdown(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleLockdown = async () => {
    setBusy("lockdown");
    try {
      if (lockdown?.enabled) {
        const { data } = await axios.post(`${API}/security/lockdown/disable`);
        setLockdown(data); toast.success("Lockdown lifted — outbound actions re-enabled");
      } else {
        if (!window.confirm("Activate LOCKDOWN? All SSH, deployments, DigitalOcean, Cloudflare and backup actions will be blocked app-wide until an admin lifts it.")) { setBusy(""); return; }
        const { data } = await axios.post(`${API}/security/lockdown/enable`, { reason: "Manual activation from Emergency panel" });
        setLockdown(data); toast.success("Lockdown ACTIVE — all outbound actions blocked");
      }
    } catch (e) {
      if (e.message !== "Step-up cancelled") toast.error("Lockdown change failed");
    } finally { setBusy(""); }
  };

  const revokeSessions = async () => {
    if (!window.confirm("Sign out EVERYONE (including you) on all devices? Use this if you suspect a stolen session.")) return;
    setBusy("revoke");
    try {
      await axios.post(`${API}/security/revoke-sessions`);
      toast.success("All sessions revoked — redirecting to login");
      setTimeout(() => { localStorage.removeItem("token"); window.location.href = "/login"; }, 1200);
    } catch (e) {
      if (e.message !== "Step-up cancelled") toast.error("Revoke failed");
    } finally { setBusy(""); }
  };

  const rotateKey = async () => {
    if (!window.confirm("Rotate the deploy SSH key on the production server? A new keypair is generated, installed and verified before the old one is removed.")) return;
    setBusy("rotate"); setRotateLog(null);
    try {
      const { data } = await axios.post(`${API}/security/rotate-ssh-key`);
      setRotateLog(data.log || []);
      data.success ? toast.success("SSH key rotated successfully") : toast.error("Rotation failed — see log");
    } catch (e) {
      if (e.message !== "Step-up cancelled") toast.error("Rotation failed");
    } finally { setBusy(""); }
  };

  return (
    <div className="space-y-4" data-testid="emergency-panel">
      <Card className={`border ${lockdown?.enabled ? "bg-red-950/30 border-red-500/40" : "bg-[#0c0c0e] border-white/5"}`}>
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2">
            {lockdown?.enabled ? <Lock className="w-4 h-4 text-red-400" /> : <Unlock className="w-4 h-4 text-emerald-400" />}
            Lockdown Kill-Switch
            {lockdown?.enabled && <span className="text-[10px] bg-red-500/20 text-red-300 rounded-full px-2 py-0.5 animate-pulse">ACTIVE</span>}
          </CardTitle>
          <CardDescription>
            {lockdown?.enabled
              ? `Blocked since ${new Date(lockdown.at).toLocaleString()} by ${lockdown.activated_by} — ${lockdown.reason}`
              : "Instantly blocks every outbound action (SSH, deploys, DigitalOcean, Cloudflare, backups). Lifting it requires fresh passkey/2FA."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={toggleLockdown} disabled={busy === "lockdown"}
                  className={lockdown?.enabled ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}
                  data-testid="lockdown-toggle-btn">
            {busy === "lockdown" ? <Loader2 className="w-4 h-4 animate-spin" /> :
              lockdown?.enabled ? "Lift lockdown" : "Activate lockdown"}
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-[#0c0c0e] border-white/5">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2">
            <UserX className="w-4 h-4 text-amber-400" /> Revoke All Sessions
          </CardTitle>
          <CardDescription>Instantly signs out every user on every device. Use if you suspect a stolen login/session.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={revokeSessions} disabled={busy === "revoke"} variant="outline"
                  className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10" data-testid="revoke-sessions-btn">
            {busy === "revoke" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign out everyone"}
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-[#0c0c0e] border-white/5">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2">
            <RotateCcw className="w-4 h-4 text-blue-400" /> Rotate Deploy SSH Key
          </CardTitle>
          <CardDescription>
            Generates a new keypair, installs it on the production server, verifies it works, then removes the old key. Fully automatic.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button onClick={rotateKey} disabled={busy === "rotate"} className="bg-blue-600 hover:bg-blue-700" data-testid="rotate-ssh-key-btn">
            {busy === "rotate" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Rotate key now"}
          </Button>
          {rotateLog && (
            <div className="bg-black/50 rounded-lg p-3 font-mono text-xs text-zinc-300 space-y-1" data-testid="rotate-log">
              {rotateLog.map((l, i) => <div key={i}>• {l}</div>)}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-[#0c0c0e] border-white/5">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-zinc-400" /> Manual Rotation Checklist
          </CardTitle>
          <CardDescription>If you believe the app was fully compromised, also rotate these (5–10 min each):</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="text-sm text-zinc-400 space-y-1.5 list-disc pl-5">
            <li><b className="text-zinc-200">DigitalOcean token</b> — cloud.digitalocean.com → API → regenerate, paste in Cloud settings</li>
            <li><b className="text-zinc-200">Cloudflare token</b> — dash.cloudflare.com → My Profile → API Tokens → roll, paste in Deploy settings</li>
            <li><b className="text-zinc-200">DO Spaces keys</b> — regenerate in Spaces settings, update in Backups page</li>
            <li><b className="text-zinc-200">Passwords</b> — change ServerAI + Odoo admin passwords (use unique ones per system)</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};
